import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Avatar } from '@/components/tree/Avatar'
import { peopleIn, type Album, type AlbumPhoto } from '@/lib/albums'
import {
  chapters,
  fan,
  OTHER_TIMES,
  PRINT_EDGE,
  PRINT_FOOT,
  rows,
  spread,
  TABLE_MAX,
  type Size,
  type Spot,
} from '@/lib/storybook'
import { cn } from '@/lib/utils'
import { StoryImg } from './PickedUp'

// How the prints lie: scattered, in one row a year, or gathered around one person.
type Lay = { by: 'spread' } | { by: 'years' } | { by: 'person'; id: string }
// A row a year shows this many prints at most; the rest of that year is counted beside it.
const PER_ROW = 8
const LABEL_W = 96
// The print last picked up lies on top of the rest.
let top = 1000

// The album as a pile of prints on the kitchen table, the way families go through old photos together: pushed
// around, picked up, turned over. One tap gathers everyone's photos of one person, or lays them out a year to a row.
export function Table({ album, onPick }: { album: Album; onPick: (photo: AlbumPhoto) => void }) {
  const area = useRef<HTMLDivElement>(null)
  const [table, setTable] = useState<Size>({ width: 0, height: 0 })
  const [lay, setLay] = useState<Lay>({ by: 'spread' })
  const cast = peopleIn(album.photos)

  useLayoutEffect(() => {
    const el = area.current
    if (!el) return
    const measure = () => setTable({ width: el.clientWidth, height: el.clientHeight })
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const { prints, labels, said } = useMemo(() => {
    const prints: { photo: AlbumPhoto; spot: Spot }[] = []
    const labels: { text: string; top: number; more: number; x: number }[] = []
    let said = ''
    if (!table.width || !table.height) return { prints, labels, said }
    if (lay.by === 'spread') {
      const shown = album.photos.slice(0, TABLE_MAX)
      spread(shown, table).forEach((spot, i) => prints.push({ photo: shown[i]!, spot }))
      said =
        album.photos.length > TABLE_MAX
          ? `${TABLE_MAX} of the ${album.photos.length} photos are on the table. Lay them out by year to see the rest.`
          : 'Push the prints around. Tap one to pick it up.'
    } else if (lay.by === 'years') {
      const years = chapters(album.photos)
      const shown = years.map((x) => x.photos.slice(0, PER_ROW))
      rows(shown, table, LABEL_W).forEach((row, g) => {
        row.spots.forEach((spot, i) => prints.push({ photo: shown[g]![i]!, spot }))
        const end = row.spots.at(-1)
        labels.push({
          text: years[g]!.year ?? OTHER_TIMES,
          top: row.top,
          more: years[g]!.photos.length - shown[g]!.length,
          x: end ? end.x + end.width + 8 : LABEL_W,
        })
      })
      said = years.some((x) => x.year === null)
        ? 'One row a year. Photos with no date yet keep a row of their own.'
        : 'One row a year, oldest at the top.'
    } else {
      const person = cast.find((x) => x.id === lay.id)
      const theirs = album.photos.filter((p) => p.people.some((x) => x.id === lay.id))
      const shown = theirs.slice(0, 12)
      fan(shown, table).forEach((spot, i) => prints.push({ photo: shown[i]!, spot }))
      said = `${person?.name ?? 'This person'} is in ${theirs.length} of the ${album.photos.length} photos.`
      if (theirs.length > shown.length) said += ` Here are the first ${shown.length}.`
    }
    return { prints, labels, said }
  }, [album.photos, cast, lay, table])

  return (
    <div
      className="flex h-full flex-col bg-table pt-[calc(var(--safe-top)+3.75rem)] pb-[calc(var(--safe-bottom)+0.5rem)]"
      data-testid="story-table"
    >
      <div ref={area} className="relative min-h-0 flex-1">
        <div className="sb-table">
          {labels.map((label) => (
            <p
              key={label.text}
              className="absolute left-4 eyebrow text-muted-foreground"
              style={{ top: label.top + 4 }}
            >
              {label.text}
            </p>
          ))}
          {labels.map((label) =>
            label.more > 0 ? (
              <p
                key={`${label.text}-more`}
                className="absolute text-xs text-muted-foreground"
                style={{ top: label.top + 24, left: Math.min(label.x, table.width - 64) }}
              >
                +{label.more}
              </p>
            ) : null,
          )}
          {prints.map(({ photo, spot }, i) => (
            <Print key={photo.id} photo={photo} spot={spot} z={i + 1} onPick={onPick} />
          ))}
        </div>
      </div>
      <div className="flex flex-col gap-2 px-3 pt-2">
        <p className="min-h-5 text-center text-sm text-muted-foreground" aria-live="polite">
          {said}
        </p>
        <div
          className="-mx-3 flex [scrollbar-width:none] gap-1.5 overflow-x-auto px-3 py-0.5"
          role="group"
          aria-label="Lay the prints out"
        >
          <Chip on={lay.by === 'spread'} onClick={() => setLay({ by: 'spread' })}>
            Spread out
          </Chip>
          <Chip on={lay.by === 'years'} onClick={() => setLay({ by: 'years' })}>
            By year
          </Chip>
          {cast.map((person) => (
            <Chip
              key={person.id}
              on={lay.by === 'person' && lay.id === person.id}
              onClick={() => setLay({ by: 'person', id: person.id })}
              face={<Avatar person={person} className="size-6 text-[0.6875rem] shadow-none" />}
            >
              {person.name}
            </Chip>
          ))}
        </div>
      </div>
    </div>
  )
}

function Chip({
  on,
  onClick,
  face,
  children,
}: {
  on: boolean
  onClick: () => void
  face?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      className={cn(
        'flex h-9 shrink-0 items-center gap-1.5 rounded-full border border-border bg-background pr-3.5 text-sm transition-colors outline-none focus-visible:ring-[3px] focus-visible:ring-ring/60',
        face ? 'pl-1.5' : 'pl-3.5',
        on && 'border-brand bg-brand text-brand-foreground',
      )}
    >
      {face}
      {children}
    </button>
  )
}

// One print. It can be pushed anywhere on the table; a tap that doesn't move it picks it up instead.
function Print({
  photo,
  spot,
  z,
  onPick,
}: {
  photo: AlbumPhoto
  spot: Spot
  z: number
  onPick: (photo: AlbumPhoto) => void
}) {
  const el = useRef<HTMLButtonElement>(null)
  const hold = useRef<{
    x: number
    y: number
    from: { x: number; y: number }
    to: { x: number; y: number }
    moving: boolean
  } | null>(null)
  // Where the reader pushed it, until the prints are laid out some other way (a new spot).
  const [pushed, setPushed] = useState<{ spot: Spot; x: number; y: number } | null>(null)
  const at = pushed?.spot === spot ? pushed : spot
  const place = (x: number, y: number) => {
    if (el.current) el.current.style.transform = `translate(${x}px, ${y}px) rotate(${spot.turn}deg)`
  }
  return (
    <button
      ref={el}
      type="button"
      aria-label={`Pick up ${photo.caption || 'this photo'}`}
      className="sb-print"
      style={{
        width: spot.width,
        // A print's white edge is a share of its own width (a percentage would be of the table's).
        padding: `${spot.width * PRINT_EDGE}px ${spot.width * PRINT_EDGE}px ${spot.width * PRINT_FOOT}px`,
        zIndex: z,
        transform: `translate(${at.x}px, ${at.y}px) rotate(${spot.turn}deg)`,
      }}
      onPointerDown={(e) => {
        e.currentTarget.setPointerCapture(e.pointerId)
        hold.current = { x: e.clientX, y: e.clientY, from: at, moving: false, to: at }
        e.currentTarget.classList.add('held')
        e.currentTarget.style.zIndex = String(++top)
      }}
      onPointerMove={(e) => {
        const h = hold.current
        if (!h) return
        const dx = e.clientX - h.x
        const dy = e.clientY - h.y
        if (Math.hypot(dx, dy) > 5) h.moving = true
        if (!h.moving) return
        h.to = { x: h.from.x + dx, y: h.from.y + dy }
        place(h.to.x, h.to.y)
      }}
      onPointerUp={(e) => {
        const h = hold.current
        hold.current = null
        e.currentTarget.classList.remove('held')
        if (h?.moving) setPushed({ spot, ...h.to })
        else if (h) onPick(photo)
      }}
      onPointerCancel={(e) => {
        if (hold.current?.moving) setPushed({ spot, ...hold.current.to })
        hold.current = null
        e.currentTarget.classList.remove('held')
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onPick(photo)
        }
      }}
      onClick={(e) => e.preventDefault()}
    >
      <StoryImg photo={photo} sizes={`${Math.round(spot.width)}px`} />
    </button>
  )
}
